import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Sparkle, Calendar, ArrowLeft, ArrowRight, Flask, Leaf,
  CircleNotch, ShieldCheck,
} from "@phosphor-icons/react";
import DamalsHeute from "@/components/daily/DamalsHeute";
import { fetchDailyToday, fetchDailyByDate, fetchDailyDates } from "@/lib/api";

function formatDateDe(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("de-DE", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function PostCard({ post, topics, focused }) {
  const topicMeta = topics?.[post.topic];
  const accent = topicMeta?.color || (post.scientific ? "#8B7CF6" : "#7EE8B0");
  const label = topicMeta?.label || post.topic_label || "Beitrag";
  return (
    <article
      className={`eid-card overflow-hidden flex flex-col transition-shadow ${
        focused ? "ring-4 ring-[#00FA9A] ring-offset-2 ring-offset-[#0A0A0A] shadow-2xl" : ""
      }`}
      data-testid={`daily-post-${post.id}`}
      data-focused={focused ? "1" : undefined}
      style={{ borderTop: `3px solid ${accent}` }}
    >
      {post.image_url && (
        <a
          href={post.source_url || `#post-${post.id}`}
          target={post.source_url ? "_blank" : undefined}
          rel={post.source_url ? "noopener noreferrer nofollow" : undefined}
          className="block aspect-square w-full overflow-hidden bg-white/5"
          data-testid={`daily-post-image-${post.id}`}
        >
          <img
            src={post.image_url}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          />
        </a>
      )}
      <div className="px-5 pt-5 pb-2 flex items-center gap-2 flex-wrap">
        <span
          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm text-white"
          style={{ background: accent }}
        >
          {post.scientific ? <Flask size={10} weight="fill" /> : <Leaf size={10} weight="fill" />}
          {label}
        </span>
        {post.scientific && (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#3D2A78] text-white">
            Wissenschaft
          </span>
        )}
        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#8B7CF6]/10 text-[#A99AFF] border border-[#8B7CF6]/40">
          <Sparkle size={10} weight="fill" /> KI-generiert
        </span>
      </div>
      <div className="px-5 pb-5 flex-1 flex flex-col">
        <h2 className="font-display text-xl md:text-3xl font-bold tracking-tighter leading-tight" data-testid={`daily-post-title-${post.id}`}>
          {post.title}
        </h2>
        {post.teaser && (
          <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">{post.teaser}</p>
        )}
        <div className="prose prose-sm max-w-none mt-3 text-[15px] leading-relaxed text-white whitespace-pre-wrap"
             data-testid={`daily-post-body-${post.id}`}>
          {post.body}
        </div>
        {post.source_url && (
          <div className="mt-4 text-xs text-zinc-400">
            Originalquelle:{" "}
            <a
              href={post.source_url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-[#00FA9A] hover:underline"
              data-testid={`daily-post-source-${post.id}`}
            >
              {post.source_title || "Externe Forschungsmeldung"}
            </a>
          </div>
        )}
        <div className="mt-5 pt-3 border-t border-white/10 text-[11px] text-zinc-500 leading-snug">
          {post.legal_tail}
        </div>
      </div>
    </article>
  );
}

export default function DailyToday() {
  const params = useParams();
  const dateParam = params?.date;
  const [searchParams] = useSearchParams();
  const focus = searchParams.get("focus");
  const [data, setData] = useState(null);
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [d, ds] = await Promise.all([
          dateParam ? fetchDailyByDate(dateParam) : fetchDailyToday(),
          fetchDailyDates(),
        ]);
        if (cancelled) return;
        setData(d);
        setDates(ds);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dateParam]);

  // Scroll into view when arriving via ?focus=vision (sidebar "Über Bodenblick" link)
  useEffect(() => {
    if (!focus || loading) return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-focused="1"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
    return () => clearTimeout(t);
  }, [focus, loading, data]);

  const posts = data?.posts || [];
  const topics = data?.topics || {};
  const date = data?.date;

  // Use the first post's image as og:image for social sharing previews
  const ogImage = posts[0]?.image_url
    ? (posts[0].image_url.startsWith("http")
        ? posts[0].image_url
        : `${window.location.origin}${posts[0].image_url}`)
    : null;
  const ogTitle = `Heute auf Bodenblick · ${formatDateDe(date)}`;
  const ogDesc = posts[0]?.teaser || "Tägliche Beiträge zu Bienen, Acker, Weide, Wald, Klima und Markt.";

  // Find prev/next dates
  const idx = dates.findIndex((d) => d === date);
  const newer = idx > 0 ? dates[idx - 1] : null;
  const older = idx >= 0 && idx < dates.length - 1 ? dates[idx + 1] : null;

  return (
    <div className="space-y-6" data-testid="daily-today-root">
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDesc} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDesc} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        {ogImage && <meta property="og:image:width" content="1080" />}
        {ogImage && <meta property="og:image:height" content="1080" />}
        <meta name="twitter:card" content={ogImage ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDesc} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
      </Helmet>

      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eid-label inline-flex items-center gap-1.5">
            <Calendar size={11} weight="fill" />
            Heute auf Bodenblick
          </div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1.5">
            {formatDateDe(date) || "Heute"}
          </h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Drei bis fünf kurze Tagesbeiträge aus Landwirtschaft, Wald, Bienen,
            Klima und Märkten — leicht verständlich, plus ein wissenschaftlicher
            Blick.
          </p>
        </div>
        <div className="flex gap-2">
          {older && (
            <Link
              to={`/heute/${older}`}
              data-testid="daily-prev-link"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-white/10 hover:bg-white/5 rounded-sm"
            >
              <ArrowLeft size={12} /> Älter
            </Link>
          )}
          {dateParam && (
            <Link
              to="/heute"
              data-testid="daily-today-link"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-[#00FA9A] text-black hover:bg-[#33FBAE] rounded-sm"
            >
              Heute
            </Link>
          )}
          {newer && (
            <Link
              to={`/heute/${newer}`}
              data-testid="daily-next-link"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-white/10 hover:bg-white/5 rounded-sm"
            >
              Neuer <ArrowRight size={12} />
            </Link>
          )}
        </div>
      </header>

      <div
        className="eid-card p-4 flex items-start gap-2 text-xs leading-snug"
        data-testid="daily-ai-banner"
        style={{ background: "rgba(139,124,246,0.10)", borderColor: "#C8B6F0" }}
      >
        <ShieldCheck size={16} weight="fill" className="text-[#A99AFF] shrink-0 mt-0.5" />
        <p className="text-[#A99AFF]">
          <strong>Hinweis (EU-KI-VO Art. 50):</strong> Alle Tagesbeiträge auf
          dieser Seite werden mit Hilfe generativer KI (Google Gemini) automatisch
          erstellt und vor Veröffentlichung redaktionell freigegeben. Sie dienen
          ausschließlich zu <strong>Informationszwecken</strong>, können Fehler
          enthalten und müssen vor jeder Verwendung selbst geprüft werden.{" "}
          <strong>Keine Handlungsempfehlung. Keine Beratung. Keine Haftung.</strong>
        </p>
      </div>

      <DamalsHeute />

      {loading && (
        <div className="text-center py-12 text-zinc-500">
          <CircleNotch size={28} className="animate-spin inline-block" />
          <div className="mt-2 text-sm">Beiträge werden geladen …</div>
        </div>
      )}

      {!loading && posts.length === 0 && (
        <div className="eid-card p-10 text-center" data-testid="daily-empty">
          <h2 className="text-lg font-medium">Noch keine Beiträge veröffentlicht.</h2>
          <p className="text-sm text-zinc-400 mt-2 max-w-md mx-auto">
            Die nächsten Tagesbeiträge erscheinen automatisch — schau morgen
            früh wieder vorbei.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="daily-grid">
        {posts.map((p) => (
          <PostCard
            key={p.id}
            post={p}
            topics={topics}
            focused={focus === "vision" && p.topic === "vision"}
          />
        ))}
      </div>
    </div>
  );
}

